import { DomainList, DomainListItemCard } from "@/components/domain";
import { Badge } from "@/components/ui/badge";
import { useDeleteGuide, useGetGuides } from "@/hooks/guides";
import { T } from "@/i18n";
import { formatPhoneNumber } from "@/lib/utils";
import { Guide } from "@/models";
import { createFileRoute, Link } from "@tanstack/react-router";
import { format } from "date-fns";
import { PersonStanding } from "lucide-react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/(guides)/guides")({
  component: RouteComponent,
});

function RouteComponent() {
  const query = useGetGuides();

  return (
    <DomainList
      domain="guides"
      breadcrumbs={[
        {
          content: (
            <>
              <T tKey="guides.title" />{" "}
              <PersonStanding width={16} height={16} />
            </>
          ),
          href: "/guides",
        },
      ]}
      tableOptions={{
        hasActions: true,
        columns: [
          {
            header: "guides.fields.name",
          },
          {
            header: "guides.fields.phone",
          },
          {
            header: "guides.fields.email",
          },
          {
            header: "guides.fields.birthdate",
          },
          {
            header: "guides.fields.country",
          },
          {
            header: "guides.fields.language",
          },
        ],
      }}
      type="table"
      data={query.data}
      isLoading={query.isLoading}
      newLink="/guides/new"
      renderItem={(guide, index, isTable) => (
        <GuideCard guide={guide} key={index} isTable={isTable} />
      )}
    />
  );
}

function GuideCard({ guide, isTable }: { guide: Guide; isTable: boolean }) {
  const { mutateAsync } = useDeleteGuide();
  const deleteGuide = () => mutateAsync(guide.id.toString());
  const { t } = useTranslation();
  return (
    <DomainListItemCard
      table={isTable}
      domain="guides"
      title={guide.name}
      titleTableOptions={{
        tableClassName: "whitespace-nowrap",
      }}
      description={formatPhoneNumber(guide.phone)}
      descriptionTableOptions={{
        tableClassName: "whitespace-nowrap",
      }}
      viewLink={(children, key) => (
        <Link key={key} to="/guides/$id" params={{ id: guide.id.toString() }}>
          {children}
        </Link>
      )}
      editLink={(children, key) => (
        <Link
          key={key}
          to="/guides/$id/edit"
          params={{ id: guide.id.toString() }}
        >
          {children}
        </Link>
      )}
      contentMap={[
        {
          value: guide.email,
          tableClassName: "whitespace-nowrap",
        },
        {
          label: t("guides.fields.birthdate"),
          value: guide.birthdate
            ? format(new Date(guide.birthdate), "PPP")
            : "-",
        },
        {
          label: t("guides.fields.country"),
          value: guide.country ? (
            <T tKey={`countries.${guide.country}`} />
          ) : (
            "-"
          ),
        },
        {
          label: t("guides.fields.language"),
          tableClassName: "min-w-48",
          value: !guide.language?.length ? (
            "-"
          ) : (
            <div className="flex flex-wrap items-center gap-1">
              {guide.language?.map((l) => (
                <Badge className="whitespace-nowrap" key={l}>
                  {l}
                </Badge>
              ))}
            </div>
          ),
        },
      ]}
      onDelete={deleteGuide}
    />
  );
}
